import { useEffect, useMemo, useState } from "react";
import { Spinner } from "@heroui/react";
import { useGetMyChannels } from "@/hooks/useIntegration";
import {
  useGetCampaignIntegrations,
  useGetCampaignReadiness,
  useUpdateCampaignIntegrations,
} from "@/features/campaigns/hooks/useCampaign";
import CommonSelect from "@/shared/components/ui/Select/CommonSelect";

const EMAIL_CHANNELS = new Set(["EMAIL", "MAIL", "IMAP", "GOOGLE_OAUTH", "OUTLOOK"]);
const WHATSAPP_CHANNELS = new Set(["WHATSAPP"]);
const LINKEDIN_CHANNELS = new Set(["LINKEDIN"]);

function normalize(value) {
  return String(value || "").trim().toUpperCase();
}

function accountLabel(account) {
  if (
    (EMAIL_CHANNELS.has(normalize(account.channel)) || EMAIL_CHANNELS.has(normalize(account.provider))) &&
    account.email
  ) return account.email;
  const channel = normalize(account.channel || account.provider);
  if (WHATSAPP_CHANNELS.has(channel) && account.phone) return account.phone;
  if (LINKEDIN_CHANNELS.has(channel) && account.username) {
    return `@${account.username.replace(/^@/, "")}`;
  }
  const base = account.label || account.email || account.provider || account.channel || "Account";
  const suffix = account.unipile_account_id ? ` · ${String(account.unipile_account_id).slice(-6)}` : "";
  return `${base}${suffix}`;
}

const CampaignIntegrations = ({ campaignId }) => {
  const { data: channelsData, isLoading: isLoadingChannels } = useGetMyChannels();
  const { data: integrations, isLoading: isLoadingIntegrations } =
    useGetCampaignIntegrations(campaignId);
  const { data: readiness, isLoading: isLoadingReadiness } =
    useGetCampaignReadiness(campaignId);
  const { mutate: updateIntegration, isPending: isSaving } =
    useUpdateCampaignIntegrations(campaignId);
  const [values, setValues] = useState({
    emailChannelAccountId: "",
    linkedinChannelAccountId: "",
    whatsappChannelAccountId: "",
  });

  const channels = useMemo(
    () => (Array.isArray(channelsData?.result) ? channelsData.result : []),
    [channelsData?.result],
  );

  const options = useMemo(() => ({
    email: channels.filter((account) =>
      normalize(account.status) === "ACTIVE" && (
        EMAIL_CHANNELS.has(normalize(account.channel)) ||
        EMAIL_CHANNELS.has(normalize(account.provider))
      ),
    ),
    linkedin: channels.filter((account) =>
      normalize(account.status) === "ACTIVE" && (
        normalize(account.channel) === "LINKEDIN" || normalize(account.provider) === "LINKEDIN"
      ),
    ),
    whatsapp: channels.filter((account) =>
      normalize(account.status) === "ACTIVE" && (
        normalize(account.channel) === "WHATSAPP" || normalize(account.provider) === "WHATSAPP"
      ),
    ),
  }), [channels]);

  useEffect(() => {
    if (!integrations) return;
    setValues({
      emailChannelAccountId: integrations.email_channel_account_id || "",
      linkedinChannelAccountId: integrations.linkedin_channel_account_id || "",
      whatsappChannelAccountId: integrations.whatsapp_channel_account_id || "",
    });
  }, [integrations]);

  const handleChange = (field, value) => {
    const nextValue = value || "";
    setValues((current) => ({ ...current, [field]: nextValue }));
    updateIntegration({ [field]: nextValue || null });
  };

  const isLoading = isLoadingChannels || isLoadingIntegrations || isLoadingReadiness;
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-gray-200 p-4 text-xs text-gray-500">
        <Spinner size="sm" color="secondary" />
        Loading campaign integrations...
      </div>
    );
  }

  const fields = [
    {
      key: "emailChannelAccountId",
      label: "Email sender",
      empty: "Use default email account",
      options: [
        { value: "", label: "Use default email account" },
        ...options.email.map((account) => ({
          value: String(account.id),
          label: `${accountLabel(account)} (${account.status})`,
        })),
      ],
    },
    {
      key: "linkedinChannelAccountId",
      label: "LinkedIn sender",
      empty: "Use default LinkedIn account",
      options: [
        { value: "", label: "Use default LinkedIn account" },
        ...options.linkedin.map((account) => ({
          value: String(account.id),
          label: `${accountLabel(account)} (${account.status})`,
        })),
      ],
    },
    {
      key: "whatsappChannelAccountId",
      label: "WhatsApp sender",
      empty: "Use default WhatsApp account",
      options: [
        { value: "", label: "Use default WhatsApp account" },
        ...options.whatsapp.map((account) => ({
          value: String(account.id),
          label: `${accountLabel(account)} (${account.status})`,
        })),
      ],
    },
  ];

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-4 dark:border-gray-700 dark:bg-gray-900/30">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Campaign integrations</h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Choose which connected account sends this campaign. Changes apply to new enrollments.
          </p>
        </div>
        {isSaving && <Spinner size="sm" color="secondary" />}
      </div>

      <div className={`mb-3 rounded-lg border px-3 py-2 text-xs ${
        readiness?.ready
          ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/30 dark:text-emerald-300"
          : "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/70 dark:bg-amber-950/30 dark:text-amber-300"
      }`}>
        <div className="font-semibold">
          {readiness?.ready ? "Ready to run" : "Action needed before starting"}
        </div>
        {!readiness?.ready && readiness?.issues?.length ? (
          <ul className="mt-1 list-disc space-y-0.5 pl-4">
            {readiness.issues.map((item) => <li key={`${item.channel}-${item.code}`}>{item.message}</li>)}
          </ul>
        ) : (
          <p className="mt-1">Every channel used by this campaign has an available sender.</p>
        )}
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {fields.map((field) => (
          <div key={field.key} className="flex flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-200">
            <span>{field.label}</span>
            <CommonSelect
              value={values[field.key]}
              onChange={(val) => handleChange(field.key, val || "")}
              options={field.options}
              placeholder={field.empty}
              width="w-full"
              ariaLabel={field.label}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default CampaignIntegrations;
